// @flow
import React from 'react';
import Card from '@material-ui/core/Card';
import { Button, CardActions, CircularProgress } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { Link, Route, Switch, withRouter } from 'react-router-dom';
import type { RouterHistory } from 'react-router-dom';
import { GoogleIcon } from 'react-google-button/lib/icons';
import { withTranslation } from 'react-i18next';

import * as Sentry from '@sentry/browser';
import LogRocket from 'logrocket';
import { withStyles } from '@material-ui/core/styles';
import { fetchWithToken, getSaasBackendEndpoint } from '../../utils/http';
import FacebookIcon from '../../../assets/images/fb_login.png';
import AvaLogin from '../../../assets/images/work.svg';
import { authInstance, googleProvider, facebookProvider } from '../../firebase';
import { createUserSearchSocket } from '../../utils/getSocket';
import * as segment from '../../utils/segment';
import electron from '../../utils/electron';
import Footer from '../../components/Footer';

import ScribeLoginEmail from './ScribeLoginEmail';
import ScribeLoginPhone from './ScribeLoginPhone';
import ScribeLoginProfiling from './ScribeLoginProfilingContainer';

const styles = theme => ({
  illustration: {
    margin: '0 auto 28px',
    maxWidth: 320,
    width: '100%',
  },
  asGuest: {
    '& a': {
      color: theme.palette.ava.blue,
    },
    color: theme.palette.ava.grey,
    fontWeight: 600,
  },
  signinButton: {
    '&:hover': {
      opacity: 0.8,
      textDecoration: 'none',
    },
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: 24,
    display: 'flex',
    fontSize: 16,
    fontWeight: 600,
    height: 48,
    marginTop: 16,
    minWidth: 240,
    overflow: 'hidden',
    padding: 2,
    paddingRight: 28,
    position: 'relative',
    textAlign: 'left',
    textTransform: 'none',
  },
  signinLogoWrap: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'center',
    height: 48,
    marginLeft: 8,
    overflow: 'hidden',
    width: 48,
  },
  signinLogo: {
    width: 24,
    height: 24,
  },
  facebookLogo: {
    transform: 'translate(-2px, -2px)',
  },
  signinTextWrap: {
    alignItems: 'center',
    display: 'flex',
    marginLeft: 8,
    width: '100%',
  },
  or: {
    fontWeight: 600,
    marginTop: 16,
  },
  title: {
    fontWeight: 700,
  }
});

type AuthenticationError = {
  name: string,
  message: string,
};

type Props = {
  backends: Object,
  branchId: string,
  displayable: Boolean,
  classes: Object,
  error: AuthenticationError,
  fetchFeatureFlag: Function,
  firebaseLoginSuccess: Function,
  history: RouterHistory,
  location: Object,
  product: string,
  switchPlatform: Function,
  t: Object,
  toggleDevMode: Function,
  token: string,
};

type State = {
  accountExistsProviders: {
    google: boolean,
    facebook: boolean,
    email: boolean,
    phone: boolean,
  },
  accountExistsEmail: string,
  loggingIn: boolean,
  findByAvaName: string,
  trackedLoggedIn: boolean,
  askedToStartConvo: boolean,
};

class ScribeLogin extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      accountExistsEmail: '',
      accountExistsProviders: {
        google: false,
        facebook: false,
        email: false,
        phone: false,
      },
      loggingIn: localStorage.getItem('firebaseRedirectPrompted') === 'true',
      findByAvaName: '',
      trackedLoggedIn: false,
      askedToStartConvo: false,
    };
  }

  trackSignup = user => {
    const getProviderFromIdentifier = identifier => {
      let signUpMethodNameLowercase = identifier.replace('.com', '');
      if (signUpMethodNameLowercase === "password") {
        signUpMethodNameLowercase = "email";
      }
      // Capitalize first char
      return (
        signUpMethodNameLowercase.charAt(0).toUpperCase() +
        signUpMethodNameLowercase.slice(1)
      );
    };
    // Signup method is the only parse
    // email we have right now
    const providers =
      user && user.parse && user.parse.emails
        ? Object.keys(user.parse.emails)
        : [];
    let signupMethod;
    if (user.parse.phoneNumber) {
      signupMethod = 'Phone';
    } else if (user.parse.emails) {
      signupMethod = getProviderFromIdentifier(
        providers[0]
      );
    }
    const createdFrom = window.isElectron ? 'Desktop' : 'Web';
    const identifyTraits = {
      'Created From': createdFrom,
      'Signed_up_at': new Date(),
      Name: user.parse.userName,
      'Ava Name': user.parse.avaName,
      Email: user.email,
      Phone: user.parse.phoneNumber,
      'Sign Up Method': signupMethod,
      referrer: undefined,
      referring_domain: undefined,
    };
    providers.forEach(identifier => {
      const provider = getProviderFromIdentifier(identifier);
      identifyTraits[`${provider} Auth`] = user.parse.emails[identifier];
    });

    // If there are utm parameters,
    // time to add them
    if (window.utm_parameters) {
      Object.keys(window.utm_parameters).forEach((utmKey) => {
        const identifyUtmKey = `utm_${utmKey === 'name' ? 'campaign': utmKey}`;
        identifyTraits[identifyUtmKey] = window.utm_parameters[utmKey];
      });
    }
    if (document.referrer) {
      identifyTraits.referrer = document.referrer;
      const tmp = document.createElement('a');
      tmp.href = document.referrer;
      identifyTraits.referring_domain = tmp.hostname;
    }

    segment.identifyWithId(
      user.parse.avaId,
      identifyTraits
    );
    // tracking Signed Up event
    segment.track('Signed Up', {
      'Created From': createdFrom,
      'Ava Name': user.parse.avaName,
      Email: user.email,
      Phone: user.parse.phoneNumber,
    });
  };

  handleNewlyLoggedInUser = async (user) => {
    const {
      fetchFeatureFlag,
      firebaseLoginSuccess,
      product
    } = this.props;
    const {
      displayName,
      email,
      emailVerified,
      photoURL,
      uid,
      isAnonymous,
    } = user;
    Sentry.addBreadcrumb({
      category: 'auth',
      message: JSON.stringify({
        displayName,
        email,
        emailVerified,
        photoURL,
        uid,
        isAnonymous,
      }),
      level: Sentry.Severity.Info,
    });

    const isNew = localStorage.getItem('avaAccount') !== 'true' && isAnonymous;
    user.getIdToken().then(idToken => {
      firebaseLoginSuccess(
        {
          displayName,
          email,
          emailVerified,
          photoURL,
          idToken,
          uid,
          isAnonymous,
        },
        isNew
      );
    });

    localStorage.setItem('avaAccount', 'true');

    Sentry.configureScope(scope => {
      scope.setUser({ email, id: uid });
    });

    const backEndUrl = localStorage.getItem('scribeBackendUrl') || '';
    if (
      backEndUrl.includes('playground') ||
      backEndUrl.includes('localhost') ||
      backEndUrl.includes('ngrok')
    ) {
      LogRocket.identify(uid, {
        email,
      });
    }

    const {
      creationTime,
      lastSignInTime
    } = authInstance.currentUser.metadata

    if (creationTime === lastSignInTime) {
      fetchWithToken(`${getSaasBackendEndpoint()}/api/v1/users/me`, {
        method: "PATCH"
      }).then(res => res.json())
      .then(res => {
        const { user: resUser } = res
        if (resUser) {
          this.trackSignup(resUser);

          window.localStorage.setItem("parseId", resUser.parse.objectId)
          window.localStorage.setItem("avaId", resUser.parse.avaId)
          this.setState({ loggingIn: false })

          const { history, location } = this.props
          if (resUser.parse.organizationId &&
            resUser.subscription.ongoingSubscription &&
            resUser.subscription.ongoingSubscription !== "Free") {
            if (location.pathname.split('/')[1] === 'web') {
              history.push('/web/login/profile/user-name-subscribed')
            } else if (location.pathname.split('/')[1] === 'scribe') {
              history.push('/scribe/login/profile/user-name-subscribed')
            }
          } else if (location.pathname.split('/')[1] === 'web') {
            history.push('/web/login/profile/user-name')
          } else if (location.pathname.split('/')[1] === 'scribe') {
            history.push('/scribe/login/profile/user-name')
          }
        } else {
          this.setState({ loggingIn: false })
        }
      })
    } else {
      let avaUser;
      if (!isAnonymous) {
        avaUser = await fetchFeatureFlag(product);
        segment.identify(avaUser.parse.avaId);
      }
      const { trackedLoggedIn } = this.state;
      if (!trackedLoggedIn) {
        this.setState({ trackedLoggedIn: true });
        segment.track('Signed In');
      }
      await this.switchProduct(avaUser || {});
      this.setState({ loggingIn: false })
    }
  }

  componentDidMount = async () => {
    // HUGE HACK FIX ME TODO, have seperate html pages/bundles for webapp scribe app
    // $FlowFixMe
    document.querySelector('html').style.fontSize = 'inherit';

    createUserSearchSocket();

    const request = localStorage.getItem('ava_request');
    if (request) {
      // localStorage.removeItem('ava_request');
      this.setState({ findByAvaName: request });
    }

    authInstance.getRedirectResult().then(async result => {
      if (result.user) {
        localStorage.removeItem('firebaseRedirectPrompted');
        await this.handleNewlyLoggedInUser(result.user)
      } else {
        authInstance.onAuthStateChanged(async user => {
          localStorage.removeItem('firebaseRedirectPrompted');
          if (user && user.emailVerified) {
            await this.handleNewlyLoggedInUser(user)
          } else {
            this.setState({ loggingIn: false })
          }
        })
      }
    }).catch(error => {
      if (error.code === "auth/account-exists-with-different-credential") {
        authInstance.fetchSignInMethodsForEmail(error.email)
        .then(providers => {
          const { history, location } = this.props
          const providersObject = {
            facebook: false,
            google: false,
            email: false,
            phone: false
          }
          providers.forEach(provider => {
            switch (provider) {
              case "facebook.com":
                providersObject.facebook = true
                break
              case "google.com":
                providersObject.google = true
                break
              case "password":
                providersObject.email = true
                break
              case "phone":
                providersObject.phone = true
                break
              default:
            }
          })
          this.setState({
            loggingIn: false,
            accountExistsProviders: providersObject,
            accountExistsEmail: error.email
          })
          if (location.pathname.split('/')[1] === 'web') {
            history.push("/web/login/account-exists")
          } else if (location.pathname.split('/')[1] === 'scribe') {
            history.push("/scribe/login/account-exists")
          }
        })
      } else {
        this.setState({
          loggingIn: false,
        })
      }
    })
  };

  switchProduct = async user => {
    const { findByAvaName, askedToStartConvo } = this.state;
    const { switchPlatform, history, product, token, branchId } = this.props;
    const { features } = user;

    if (
      features &&
      features.scribe &&
      !token &&
      !localStorage.getItem('currentMode') === 'scribe'
    ) {
      return switchPlatform('web', history);
    }
    if (
      features &&
      ((features.scribe &&
        product === 'scribe' &&
        features['scribe-roger-voice']) ||
        (!features['ava-webapp'] && features.scribe))
    ) {
      return switchPlatform('scribe', history);
    }
    if (
      (features && features['ava-webapp']) ||
      token ||
      branchId ||
      localStorage.getItem('ava_request')
    ) {
      if (!askedToStartConvo) {
        this.setState({ askedToStartConvo: true });
        return switchPlatform('web', history, { startConvo: true });
      }
      return switchPlatform('web', history);
    }
    if (user) {
      localStorage.setItem('ava_request', findByAvaName || '');
      return switchPlatform('web', history, {
        request: { avaName: findByAvaName },
      });
    }
    return history.push('/download');
  };

  login = (provider) => {
    this.setState({ loggingIn: true });
    authInstance.signInWithRedirect(provider)
    localStorage.setItem('firebaseRedirectPrompted', 'true');
  };

  render() {
    const {
      backends,
      classes,
      displayable,
      error,
      location,
      product,
      t,
      toggleDevMode,
    } = this.props;
    const {
      accountExistsProviders,
      accountExistsEmail,
      loggingIn,
    } = this.state;

    let heightOffset = '';
    if (window.isElectron) {
      const { remote } = electron;
      if (remote.process.platform === 'darwin') {
        heightOffset = ' - 28px'
      } else {
        heightOffset = ' - 32px'
      }
    }

    const productPath = location.pathname.split('/')[1]

    return (
      <div
        style={{
          height: `calc(var(--vh, 1vh) * 100${heightOffset})`,
          overflow: 'scroll',
        }}
      >
        <div
          style={{
            paddingBottom: 48,
            paddingTop: 96,
            maxWidth: 600,
            marginLeft: 'auto',
            marginRight: 'auto',
            display: 'flex',
            flexDirection: 'column',
            width: '85%',
          }}
        >
          <div style={{ flexGrow: 1 }} />
          {loggingIn && <CircularProgress color="secondary" style={{ margin: '0 auto' }} />}
          {!loggingIn && (
            <>
              {error.name && (
                <>
                  <Typography align="center">
                    <i
                      css={{ textalign: 'center' }}
                      className="material-icons"
                    >
                      offline_bolt
                    </i>
                  </Typography>
                  <Typography variant="subtitle1" align="right">
                    {error.name}
                  </Typography>
                </>
              )}
              <Switch>
                <Route
                  path={["/web/login/email", "/web/login/email"]}
                  render={() => {
                    return (
                      <ScribeLoginEmail
                        handleNewlyLoggedInUser={this.handleNewlyLoggedInUser} />
                    )
                  }}
                />
                <Route
                  path={["/web/login/phone", "/scribe/login/phone"]}
                  render={() => {
                    return (
                      <ScribeLoginPhone
                        handleNewlyLoggedInUser={this.handleNewlyLoggedInUser} />
                    )
                  }}
                />
                <Route
                  path={["/web/login/profile", "/scribe/login/profile"]}
                  render={() => <ScribeLoginProfiling product={product} />}
                />
                <Route
                  exact
                  path={["/web/login/account-exists", "/scribe/login/account-exists"]}
                  render={() => {
                    return (
                      <Card
                        style={{
                          boxShadow: 'none',
                          marginBottom: '20px',
                        }}
                      >
                        <CardActions>
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            margin: '0 auto 12px',
                            maxWidth: 380,
                          }}>
                            <Typography
                              variant="h4"
                              className={classes.title}
                              align="center"
                              gutterBottom
                            >
                              {t('signin-messages.accountExistsHead')}
                            </Typography>
                            <Typography
                              variant="body1"
                              align="center"
                              gutterBottom
                            >
                              {t('signin-messages.accountExistsBody', { email: accountExistsEmail })}
                            </Typography>
                            {accountExistsProviders.facebook && (
                              <Button
                                className={classes.signinButton}
                                style={{
                                  backgroundColor: '#1876f2',
                                  color: 'white',
                                }}
                                onClick={() => this.login(facebookProvider)}
                              >
                                <div className={`${classes.signinLogoWrap} ${classes.facebookLogo}`}>
                                  <img
                                    className={classes.signinLogo}
                                    src={FacebookIcon}
                                    alt="Facebook logo"
                                     />
                                </div>
                                <span className={classes.signinTextWrap}>{t('signInWithFacebook')}</span>
                              </Button>
                            )}
                            {accountExistsProviders.google && (
                              <Button
                                className={classes.signinButton}
                                style={{
                                  backgroundColor: 'white',
                                  border: 'solid 1px #dddddd',
                                }}
                                onClick={() => this.login(googleProvider)}
                              >
                                <div className={`${classes.signinLogoWrap} ${classes.googleLogo}`}>
                                  <GoogleIcon />
                                </div>
                                <span className={classes.signinTextWrap}>{t('signInWithGoogle')}</span>
                              </Button>
                            )}
                            {accountExistsProviders.email && (
                              <Link
                                className={classes.signinButton}
                                style={{
                                  backgroundColor: '#77B8EC',
                                  color: 'white',
                                }}
                                to="login/email"
                              >
                                <div className={classes.signinLogoWrap}>
                                  <i className="material-icons">email</i>
                                </div>
                                <span className={classes.signinTextWrap}>{t('signInWithEmail')}</span>
                              </Link>
                            )}
                            {accountExistsProviders.phone && (
                              <Link
                                className={classes.signinButton}
                                style={{
                                  backgroundColor: '#64C25E',
                                  color: 'white',
                                }}
                                to="login/phone"
                              >
                                <div className={classes.signinLogoWrap}>
                                  <i className="material-icons">phone</i>
                                </div>
                                <span className={classes.signinTextWrap}>{t('signInWithPhone')}</span>
                              </Link>
                            )}
                          </div>
                        </CardActions>
                      </Card>
                    )
                  }} />
                <Route
                  exact
                  path={["/web/login", "/scribe/login"]}
                  render={() => {
                    return (
                      <Card
                        style={{
                          boxShadow: 'none',
                          marginBottom: '20px',
                        }}
                      >
                        <CardActions>
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            margin: '0 auto 12px',
                          }}>
                            <img
                              src={AvaLogin}
                              alt="Laptop with Ava captioning on screen"
                              className={classes.illustration}
                            />
                            <Typography
                              variant="h4"
                              className={classes.title}
                              align="center"
                              gutterBottom
                            >
                              {t('signInSignUp')}
                            </Typography>
                            <Button
                              className={classes.signinButton}
                              style={{
                                backgroundColor: '#1876f2',
                                color: 'white',
                              }}
                              onClick={() => this.login(facebookProvider)}
                            >
                              <div className={`${classes.signinLogoWrap} ${classes.facebookLogo}`}>
                                <img
                                  className={classes.signinLogo}
                                  src={FacebookIcon}
                                  alt="Facebook logo"
                                   />
                              </div>
                              <span className={classes.signinTextWrap}>{t('signInWithFacebook')}</span>
                            </Button>
                            <Button
                              className={classes.signinButton}
                              style={{
                                backgroundColor: 'white',
                                border: 'solid 1px #dddddd',
                              }}
                              onClick={() => this.login(googleProvider)}
                            >
                              <div className={`${classes.signinLogoWrap} ${classes.googleLogo}`}>
                                <GoogleIcon />
                              </div>
                              <span className={classes.signinTextWrap}>{t('signInWithGoogle')}</span>
                            </Button>
                            <Typography className={classes.or} variant="body2" color="textSecondary" align="center">
                              {t('or')}
                            </Typography>
                            <Link
                              className={classes.signinButton}
                              style={{
                                backgroundColor: '#77B8EC',
                                color: 'white',
                              }}
                              to={`/${productPath}/login/email`}
                            >
                              <div className={classes.signinLogoWrap}>
                                <i className="material-icons">email</i>
                              </div>
                              <span className={classes.signinTextWrap}>{t('signInWithEmail')}</span>
                            </Link>
                            <Link
                              className={classes.signinButton}
                              style={{
                                backgroundColor: '#64C25E',
                                color: 'white',
                              }}
                              to={`/${productPath}/login/phone`}
                            >
                              <div className={classes.signinLogoWrap}>
                                <i className="material-icons">phone</i>
                              </div>
                              <span className={classes.signinTextWrap}>{t('signInWithPhone')}</span>
                            </Link>
                          </div>
                        </CardActions>
                        <Typography variant="subtitle1" align="center" className={classes.asGuest}>
                          <span>{`${t('joinAConversation')} `}</span>
                          <Link to="/web">
                            {t('asAGuest')}
                          </Link>
                        </Typography>
                      </Card>
                    )
                  }} />
              </Switch>
            </>
          )}
          {error && error.message && (
            <Typography variant="subtitle1" align="center">
              {error.message && error.message}
            </Typography>
          )}
          <div style={{ flexGrow: 3 }} />
        </div>
        <Footer
          toggleDevMode={toggleDevMode}
          displayable={displayable}
          backends={backends}
        />
      </div>
    );
  }
}

// $FlowFixMe
export default withTranslation()(withStyles(styles)(withRouter(ScribeLogin)));
